import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { fetchUserPermissions, fetchOrgFeatureFlags } from '../actioncreators/actioncreators';

// Create the context
const PermissionContext = createContext(null);

// Permission codes for menus (matching SQL seed data)
export const MENU_PERMISSIONS = {
  DASHBOARD: 'MENU_DASHBOARD',
  ANALYTICS: 'MENU_ANALYTICS',
  TIMESHEETS: 'MENU_TIMESHEETS',
  USERS: 'MENU_USERS',
  WORK_SCHEDULE: 'MENU_WORK_SCHEDULE',
  LOCATIONS: 'MENU_LOCATIONS',
  TRACKING: 'MENU_TRACKING',
  MOBILE_APP_USAGE: 'MENU_MOBILE_APP_USAGE',
  MOBILE_APP_SETUP: 'MENU_MOBILE_APP_SETUP',
  WORK_ASSIGNMENT: 'MENU_WORK_ASSIGNMENT',
  APPROVALS: 'MENU_APPROVALS',
  REPORTS: 'MENU_REPORTS',
  ORGANIZATION: 'MENU_ORGANIZATION',
  SUPER_ADMIN: 'MENU_SUPER_ADMIN',
};

// Screen permission codes (matching SQL seed data)
export const SCREEN_PERMISSIONS = {
  DASHBOARD_VIEW: 'SCREEN_DASHBOARD_VIEW',
  ANALYTICS_VIEW: 'SCREEN_ANALYTICS_VIEW',
  TIMESHEETS_VIEW: 'SCREEN_TIMESHEETS_VIEW',
  USERS_VIEW: 'SCREEN_USERS_VIEW',
  USER_DETAILS_VIEW: 'SCREEN_USER_DETAILS_VIEW',
  LOCATIONS_VIEW: 'SCREEN_LOCATIONS_VIEW',
  TRACKING_VIEW: 'SCREEN_TRACKING_VIEW',
  REPORTS_VIEW: 'SCREEN_REPORTS_VIEW',
  APPROVALS_VIEW: 'SCREEN_APPROVALS_VIEW',
  APPROVAL_DETAIL_VIEW: 'SCREEN_APPROVAL_DETAIL_VIEW',
  SETTINGS_VIEW: 'SCREEN_SETTINGS_VIEW',
  SUB_ORG_MANAGE: 'SCREEN_SUB_ORG_MANAGE',
  SUPER_ADMIN_DASHBOARD: 'SCREEN_SUPER_ADMIN_DASHBOARD',
  SUPER_ADMIN_ORGS: 'SCREEN_SUPER_ADMIN_ORGS',
  SUPER_ADMIN_USERS: 'SCREEN_SUPER_ADMIN_USERS',
  SUPER_ADMIN_PLANS: 'SCREEN_SUPER_ADMIN_PLANS',
  SUPER_ADMIN_AUDIT: 'SCREEN_SUPER_ADMIN_AUDIT',
};

// Feature permission codes (matching SQL seed data)
export const FEATURE_PERMISSIONS = {
  // Users/Members
  USERS_VIEW: 'FEATURE_USERS_VIEW',
  USERS_CREATE: 'FEATURE_USERS_CREATE',
  USERS_EDIT: 'FEATURE_USERS_EDIT',
  USERS_DELETE: 'FEATURE_USERS_DELETE',
  USERS_ASSIGN: 'FEATURE_USERS_ASSIGN',
  // Timesheets
  TIMESHEETS_VIEW: 'FEATURE_TIMESHEETS_VIEW',
  TIMESHEETS_EDIT: 'FEATURE_TIMESHEETS_EDIT',
  TIMESHEETS_APPROVE: 'FEATURE_TIMESHEETS_APPROVE',
  TIMESHEETS_EXPORT: 'FEATURE_TIMESHEETS_EXPORT',
  // Locations
  LOCATIONS_VIEW: 'FEATURE_LOCATIONS_VIEW',
  LOCATIONS_CREATE: 'FEATURE_LOCATIONS_CREATE',
  LOCATIONS_EDIT: 'FEATURE_LOCATIONS_EDIT',
  LOCATIONS_DELETE: 'FEATURE_LOCATIONS_DELETE',
  // Tracking
  TRACKING_VIEW: 'FEATURE_TRACKING_VIEW',
  TRACKING_LIVE: 'FEATURE_TRACKING_LIVE',
  // Reports
  REPORTS_VIEW: 'FEATURE_REPORTS_VIEW',
  REPORTS_GENERATE: 'FEATURE_REPORTS_GENERATE',
  REPORTS_EXPORT: 'FEATURE_REPORTS_EXPORT',
  // Approvals
  APPROVALS_VIEW: 'FEATURE_APPROVALS_VIEW',
  APPROVALS_APPROVE: 'FEATURE_APPROVALS_APPROVE',
  APPROVALS_ESCALATE: 'FEATURE_APPROVALS_ESCALATE',
  // Work Schedule
  WORK_SCHEDULE_VIEW: 'FEATURE_WORK_SCHEDULE_VIEW',
  WORK_SCHEDULE_CREATE: 'FEATURE_WORK_SCHEDULE_CREATE',
  WORK_SCHEDULE_EDIT: 'FEATURE_WORK_SCHEDULE_EDIT',
  WORK_SCHEDULE_DELETE: 'FEATURE_WORK_SCHEDULE_DELETE',
  // Work Assignment
  WORK_ASSIGNMENT_VIEW: 'FEATURE_WORK_ASSIGNMENT_VIEW',
  WORK_ASSIGNMENT_CREATE: 'FEATURE_WORK_ASSIGNMENT_CREATE',
  WORK_ASSIGNMENT_EDIT: 'FEATURE_WORK_ASSIGNMENT_EDIT',
  // Organization
  ORGANIZATION_VIEW: 'FEATURE_ORGANIZATION_VIEW',
  ORGANIZATION_EDIT: 'FEATURE_ORGANIZATION_EDIT',
  ORGANIZATION_MANAGE_ROLES: 'FEATURE_ORGANIZATION_MANAGE_ROLES',
  ORGANIZATION_MANAGE_PERMISSIONS: 'FEATURE_ORGANIZATION_MANAGE_PERMISSIONS',
};

// Feature flag codes (subscription-based, matching SQL feature_flag table)
export const FEATURE_FLAGS = {
  BASIC_TIMESHEETS: 'basic_timesheets',
  BASIC_MEMBERS: 'basic_members',
  BASIC_LOCATIONS: 'basic_locations',
  ANALYTICS_DASHBOARD: 'analytics_dashboard',
  ADVANCED_REPORTS: 'advanced_reports',
  LIVE_TRACKING: 'live_tracking',
  APPROVAL_WORKFLOW: 'approval_workflow',
  CUSTOM_ROLES: 'custom_roles',
  API_ACCESS: 'api_access',
  SSO_INTEGRATION: 'sso_integration',
  WHITE_LABEL: 'white_label',
};

// Hierarchy levels
export const HIERARCHY_LEVELS = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MANAGER: 40,
  MEMBER: 20,
};

// Provider component
export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [dataScope, setDataScope] = useState('SELF');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tracks a pending quick-retry so a transient bootstrap failure (e.g. the
  // auth cookie not yet attached on the first request after a redirect) is
  // recovered within ~1.5s instead of waiting for the 30s poll.
  const retryTimerRef = useRef(null);

  const loginResponse = useSelector((state) => state.loginResponse);
  const userRole = loginResponse?.role_name || loginResponse?.roleName || 'MEMBER';
  const userId = loginResponse?.user_id || loginResponse?.userId || '';
  const orgId = loginResponse?.org_id || loginResponse?.orgId || '';

  // Load permissions when user logs in. isBackgroundRefresh=true (used by the
  // 30s poll below) skips the loading-overlay flag - without this, the
  // full-screen blocking overlay reappeared and intercepted clicks every 30
  // seconds for an already-logged-in user with valid permissions, even though
  // this poll is meant to be an invisible background refresh (see its own
  // comment: "without needing to re-login").
  const loadPermissions = useCallback(async (isBackgroundRefresh = false) => {
    console.log('[PermissionContext] loadPermissions called', { userId, orgId, userRole, isBackgroundRefresh });

    // Never fetch permissions on public / auth pages (login, registration, KYC
    // callback). PermissionProvider wraps the whole app, so without this guard
    // a stale userId/orgId left in the store would fire my-permissions/features
    // on the login page and 401 in a loop.
    const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
    const onAuthPage =
      !path.startsWith('/home') ||
      path === '/' ||
      path === '/registration' ||
      path.startsWith('/login') ||
      path.startsWith('/digitalKYC');
    if (onAuthPage) {
      setPermissions(getDefaultPermissionsForRole(userRole));
      setLoading(false);
      return;
    }

    if (!userId || !orgId) {
      console.log('[PermissionContext] Missing userId or orgId, using fallback permissions');
      setPermissions(getDefaultPermissionsForRole(userRole));
      setLoading(false);
      return;
    }

    try {
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      setError(null);

      console.log('[PermissionContext] Fetching permissions from API...');
      // Fetch user permissions and feature flags in parallel
      const [permResponse, featureResponse] = await Promise.all([
        fetchUserPermissions(),
        fetchOrgFeatureFlags()
      ]);

      console.log('[PermissionContext] API Response:', { permResponse: permResponse?.data, featureResponse: featureResponse?.data });

      if (permResponse?.data?.success) {
        const apiPermissions = permResponse.data.data.permissions || [];
        setDataScope(permResponse.data.data.data_scope || 'SELF');
        // If API returned empty permissions, use role-based fallback so all tabs show
        if (apiPermissions.length === 0) {
          setPermissions(getDefaultPermissionsForRole(userRole));
        } else {
          setPermissions(apiPermissions);
        }
      } else {
        console.log('[PermissionContext] API did not return success, using fallback');
        setPermissions(getDefaultPermissionsForRole(userRole));
      }

      if (featureResponse?.data?.success) {
        setFeatures(featureResponse.data.data || []);
      }
      // Successful load — clear any pending retry.
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    } catch (err) {
      console.error('[PermissionContext] Failed to load permissions:', err);
      setError(err.message);
      // Set default permissions based on role as fallback so the UI is usable.
      setPermissions(getDefaultPermissionsForRole(userRole));

      // Only retry on a genuinely transient failure where the auth cookie may
      // not have been attached yet (no response / network error). A 401 here
      // means the request reached the server unauthenticated — retrying would
      // just loop, so we leave it for the 30s poll / next user action.
      const status = err?.response?.status;
      const isTransient = !err?.response; // network / no-response error only
      if (isTransient && !retryTimerRef.current && status !== 401) {
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          loadPermissions();
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, orgId, userRole]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Clear any pending quick-retry timer on unmount.
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // Poll for permission changes every 30 seconds so already-logged-in users
  // pick up role permission updates without needing to re-login. Runs in the
  // background (isBackgroundRefresh=true) so it never re-shows the full-screen
  // loading overlay for a user who is already logged in and interacting with
  // the app.
  useEffect(() => {
    if (!userId || !orgId) return;
    const interval = setInterval(() => {
      loadPermissions(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [userId, orgId, loadPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionCode) => {
    // Super admin has all permissions
    if (userRole === 'SUPER_ADMIN') return true;

    return permissions.some(p => p.permission_code === permissionCode);
  }, [permissions, userRole]);

  // Check if user has any of the given permissions
  const hasAnyPermission = useCallback((permissionCodes) => {
    if (userRole === 'SUPER_ADMIN') return true;

    return permissionCodes.some(code =>
      permissions.some(p => p.permission_code === code)
    );
  }, [permissions, userRole]);

  // Check if user has all of the given permissions
  const hasAllPermissions = useCallback((permissionCodes) => {
    if (userRole === 'SUPER_ADMIN') return true;

    return permissionCodes.every(code =>
      permissions.some(p => p.permission_code === code)
    );
  }, [permissions, userRole]);

  // Check if a feature is enabled for the organization
  const isFeatureEnabled = useCallback((featureCode) => {
    const feature = features.find(f => f.feature_code === featureCode);
    return feature?.is_enabled || false;
  }, [features]);

  // Check if user has minimum hierarchy level
  const hasMinimumRole = useCallback((requiredRole) => {
    const userLevel = HIERARCHY_LEVELS[userRole] || 0;
    const requiredLevel = HIERARCHY_LEVELS[requiredRole] || 100;
    return userLevel >= requiredLevel;
  }, [userRole]);

  // Get permission scope for a specific permission
  const getPermissionScope = useCallback((permissionCode) => {
    const permission = permissions.find(p => p.permission_code === permissionCode);
    return permission?.granted_scope || dataScope;
  }, [permissions, dataScope]);

  // Check if user can access a menu item
  const canAccessMenu = useCallback((menuCode) => {
    // Super admin can access everything
    if (userRole === 'SUPER_ADMIN') return true;

    // Check for menu permission
    const menuPermCode = `MENU_${menuCode.toUpperCase().replace(/\s+/g, '_')}`;
    return hasPermission(menuPermCode);
  }, [userRole, hasPermission]);

  // Refresh permissions (can be called after role changes)
  const refreshPermissions = useCallback(() => {
    loadPermissions();
  }, [loadPermissions]);

  const value = {
    // State
    permissions,
    features,
    dataScope,
    loading,
    error,
    userRole,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isFeatureEnabled,
    hasMinimumRole,
    getPermissionScope,
    canAccessMenu,

    // Actions
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook to use permission context
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Helper function to get default permissions based on role (fallback)
const getDefaultPermissionsForRole = (role) => {
  const basePermissions = [
    { permission_code: 'MENU_DASHBOARD', permission_type: 'MENU' },
    { permission_code: 'SCREEN_DASHBOARD_VIEW', permission_type: 'SCREEN' },
  ];

  switch (role) {
    case 'SUPER_ADMIN':
      // Super admin gets everything - handled in hasPermission
      return [];

    case 'ADMIN':
      return [
        ...basePermissions,
        { permission_code: 'MENU_ANALYTICS', permission_type: 'MENU' },
        { permission_code: 'MENU_TIMESHEETS', permission_type: 'MENU' },
        { permission_code: 'MENU_USERS', permission_type: 'MENU' },
        { permission_code: 'MENU_WORK_SCHEDULE', permission_type: 'MENU' },
        { permission_code: 'MENU_LOCATIONS', permission_type: 'MENU' },
        { permission_code: 'MENU_TRACKING', permission_type: 'MENU' },
        { permission_code: 'MENU_MOBILE_APP_USAGE', permission_type: 'MENU' },
        { permission_code: 'MENU_MOBILE_APP_SETUP', permission_type: 'MENU' },
        { permission_code: 'MENU_WORK_ASSIGNMENT', permission_type: 'MENU' },
        { permission_code: 'MENU_REPORTS', permission_type: 'MENU' },
        { permission_code: 'MENU_ORGANIZATION', permission_type: 'MENU' },
        { permission_code: 'SCREEN_USERS_MANAGE', permission_type: 'SCREEN' },
        { permission_code: 'SCREEN_SETTINGS_VIEW', permission_type: 'SCREEN' },
        { permission_code: 'SCREEN_SETTINGS_MANAGE', permission_type: 'SCREEN' },
        { permission_code: 'SCREEN_SUB_ORG_MANAGE', permission_type: 'SCREEN' },
        { permission_code: 'FEATURE_ORGANIZATION_MANAGE_ROLES', permission_type: 'FEATURE' },
      ];

    case 'MANAGER':
      return [
        ...basePermissions,
        { permission_code: 'MENU_TIMESHEETS', permission_type: 'MENU' },
        { permission_code: 'MENU_USERS', permission_type: 'MENU' },
        { permission_code: 'MENU_TRACKING', permission_type: 'MENU' },
        { permission_code: 'MENU_WORK_ASSIGNMENT', permission_type: 'MENU' },
        { permission_code: 'MENU_REPORTS', permission_type: 'MENU' },
        { permission_code: 'MENU_ORGANIZATION', permission_type: 'MENU' },
        { permission_code: 'SCREEN_TIMESHEETS_VIEW', permission_type: 'SCREEN' },
        { permission_code: 'SCREEN_USERS_VIEW', permission_type: 'SCREEN' },
        { permission_code: 'SCREEN_SETTINGS_VIEW', permission_type: 'SCREEN' },
        { permission_code: 'SCREEN_SUB_ORG_MANAGE', permission_type: 'SCREEN' },
      ];

    case 'MEMBER':
    default:
      return [
        ...basePermissions,
        { permission_code: 'MENU_TIMESHEETS', permission_type: 'MENU' },
        { permission_code: 'SCREEN_TIMESHEETS_VIEW', permission_type: 'SCREEN' },
      ];
  }
};

export default PermissionContext;
